import React, { useState, useRef, useEffect, useCallback } from 'react';

const CHATBOT_API ='https://ngozi-university-chatbot.vercel.app';

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: "Bonjour ! Je suis Anita AI \ud83d\udc4b, votre assistante virtuelle de l'Universit\u00e9 de Ngozi. Comment puis-je vous aider aujourd'hui ?"
};

export default function AnitaAI() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(generateUUID);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isTyping) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setIsTyping(true);

    try {
      const res = await fetch(`${CHATBOT_API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, sessionId })
      });
      const data = await res.json();
      if (!res.ok || !data.reply) {
        throw new Error(data.error || 'No reply');
      }
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: "D\u00e9sol\u00e9e, je rencontre un probl\u00e8me technique. Veuillez r\u00e9essayer ou contacter l'universit\u00e9 au +257 22 30 22 59." }
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const clearConversation = async () => {
    try {
      await fetch(`${CHATBOT_API}/api/chat/clear`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
      });
    } catch {
      // Ignore errors on clear
    }
    setSessionId(generateUUID());
    setMessages([WELCOME_MESSAGE]);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <style>{`
        /* ===== Floating Button ===== */
        .anita-fab-wrapper {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 9999;
          display: flex;
          align-items: center;
          gap: 0;
        }

        .anita-tooltip {
          background: #003d80;
          color: #fff;
          padding: 8px 14px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 500;
          white-space: nowrap;
          box-shadow: 0 4px 16px rgba(0,61,128,0.25);
          margin-right: 12px;
          animation: anita-tooltip-float 3s ease-in-out infinite;
          position: relative;
        }
        .anita-tooltip::after {
          content: '';
          position: absolute;
          right: -6px;
          top: 50%;
          transform: translateY(-50%);
          border-left: 7px solid #003d80;
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
        }
        @keyframes anita-tooltip-float {
          0%, 100% { transform: translateX(0); opacity: 1; }
          50% { transform: translateX(-4px); opacity: 0.9; }
        }

        .anita-btn {
          width: 62px;
          height: 62px;
          border-radius: 50%;
          background: linear-gradient(135deg, #003d80 0%, #00BCD4 100%);
          color: #fff;
          border: none;
          cursor: pointer;
          box-shadow: 0 4px 20px rgba(0,61,128,0.35), 0 0 0 0 rgba(0,188,212,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          transition: transform 0.2s, box-shadow 0.2s;
          animation: anita-pulse 2.5s ease-in-out infinite;
          flex-shrink: 0;
          position: relative;
        }
        .anita-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 24px rgba(0,61,128,0.45);
          animation: none;
        }
        .anita-btn-open {
          animation: none;
          background: #003d80;
        }
        @keyframes anita-pulse {
          0% { box-shadow: 0 4px 20px rgba(0,61,128,0.35), 0 0 0 0 rgba(0,188,212,0.5); }
          50% { box-shadow: 0 4px 20px rgba(0,61,128,0.35), 0 0 0 12px rgba(0,188,212,0); }
          100% { box-shadow: 0 4px 20px rgba(0,61,128,0.35), 0 0 0 0 rgba(0,188,212,0); }
        }

        .anita-btn svg {
          width: 30px;
          height: 30px;
          fill: #fff;
        }

        /* ===== Chat Window ===== */
        .anita-chat {
          position: fixed;
          bottom: 100px;
          right: 24px;
          width: 390px;
          max-width: calc(100vw - 32px);
          height: 540px;
          max-height: calc(100vh - 120px);
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 8px 40px rgba(0,61,128,0.2);
          display: flex;
          flex-direction: column;
          z-index: 9999;
          overflow: hidden;
          animation: anita-slide-up 0.3s ease-out;
        }
        @keyframes anita-slide-up {
          from { opacity: 0; transform: translateY(24px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* ===== Header ===== */
        .anita-header {
          background: linear-gradient(135deg, #003d80 0%, #004e9e 100%);
          color: #fff;
          padding: 16px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }
        .anita-header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .anita-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #00BCD4 0%, #0097A7 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 17px;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        .anita-header-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .anita-header-title {
          font-weight: 700;
          font-size: 15px;
          letter-spacing: 0.2px;
        }
        .anita-header-badge {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 11.5px;
          opacity: 0.9;
        }
        .anita-online-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #4cff6e;
          display: inline-block;
          animation: anita-blink 2s ease-in-out infinite;
        }
        @keyframes anita-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .anita-clear-btn {
          background: rgba(255,255,255,0.12);
          border: none;
          color: #fff;
          cursor: pointer;
          padding: 7px 11px;
          border-radius: 8px;
          font-size: 16px;
          transition: background 0.2s;
        }
        .anita-clear-btn:hover { background: rgba(255,255,255,0.25); }

        /* ===== Messages ===== */
        .anita-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          background: #f5f8fc;
        }
        .anita-messages::-webkit-scrollbar { width: 5px; }
        .anita-messages::-webkit-scrollbar-track { background: transparent; }
        .anita-messages::-webkit-scrollbar-thumb { background: #c0cfe0; border-radius: 10px; }

        .anita-msg {
          max-width: 82%;
          padding: 10px 14px;
          border-radius: 14px;
          font-size: 14px;
          line-height: 1.55;
          word-wrap: break-word;
          white-space: pre-wrap;
        }
        .anita-msg-user {
          align-self: flex-end;
          background: linear-gradient(135deg, #003d80 0%, #00579e 100%);
          color: #fff;
          border-bottom-right-radius: 4px;
        }
        .anita-msg-assistant {
          align-self: flex-start;
          background: #fff;
          color: #1a2a3a;
          border: 1px solid #dde5ef;
          border-bottom-left-radius: 4px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
        }

        /* ===== Typing ===== */
        .anita-typing {
          align-self: flex-start;
          display: flex;
          gap: 5px;
          padding: 12px 18px;
          background: #fff;
          border-radius: 14px;
          border: 1px solid #dde5ef;
        }
        .anita-typing span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #00BCD4;
          animation: anita-bounce 1.2s infinite;
        }
        .anita-typing span:nth-child(2) { animation-delay: 0.2s; }
        .anita-typing span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes anita-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.3; }
          30% { transform: translateY(-7px); opacity: 1; }
        }

        /* ===== Input ===== */
        .anita-input-area {
          display: flex;
          padding: 12px;
          gap: 8px;
          border-top: 1px solid #e4eaf2;
          background: #fff;
          flex-shrink: 0;
        }
        .anita-input {
          flex: 1;
          border: 1.5px solid #d0daea;
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 14px;
          outline: none;
          resize: none;
          font-family: inherit;
          max-height: 80px;
          transition: border-color 0.2s;
        }
        .anita-input:focus { border-color: #00BCD4; box-shadow: 0 0 0 2px rgba(0,188,212,0.12); }
        .anita-send-btn {
          background: linear-gradient(135deg, #003d80 0%, #00BCD4 100%);
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 10px 16px;
          cursor: pointer;
          font-size: 16px;
          transition: opacity 0.2s, transform 0.15s;
          flex-shrink: 0;
        }
        .anita-send-btn:hover { opacity: 0.9; transform: scale(1.04); }
        .anita-send-btn:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }

        /* ===== Powered By ===== */
        .anita-powered {
          text-align: center;
          font-size: 10.5px;
          color: #8a9ab5;
          padding: 4px 0 8px;
          background: #fff;
        }
        .anita-powered a {
          color: #003d80;
          text-decoration: none;
          font-weight: 600;
        }

        /* ===== Header Close Button (visible on mobile only) ===== */
        .anita-close-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.12);
          border: none;
          color: #fff;
          cursor: pointer;
          padding: 7px;
          border-radius: 8px;
          font-size: 16px;
          transition: background 0.2s;
          margin-left: 8px;
        }
        .anita-close-btn:hover { background: rgba(255,255,255,0.25); }

        /* ===== Mobile Backdrop ===== */
        .anita-backdrop {
          display: none;
        }

        /* ===== Mobile ===== */
        @media (max-width: 480px) {
          .anita-backdrop {
            display: block;
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: rgba(0, 0, 0, 0.4);
            z-index: 9998;
            animation: anita-fade-in 0.25s ease-out;
          }
          @keyframes anita-fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .anita-chat {
            bottom: 0;
            right: 0;
            width: 85vw;
            height: 50vh;
            max-height: 50vh;
            border-radius: 16px 16px 0 0;
          }
          .anita-fab-wrapper { bottom: 16px; right: 16px; }
          .anita-fab-wrapper.anita-fab-hidden { display: none; }
          .anita-btn { width: 54px; height: 54px; }
          .anita-btn svg { width: 26px; height: 26px; }
          .anita-tooltip { display: none; }
          .anita-input-area { padding: 10px 12px; padding-bottom: max(10px, env(safe-area-inset-bottom)); }
          .anita-powered { padding-bottom: max(8px, env(safe-area-inset-bottom)); }
        }
      `}</style>

      {isOpen && <div className="anita-backdrop" onClick={() => setIsOpen(false)} />}

      {isOpen && (
        <div className="anita-chat">
          <div className="anita-header">
            <div className="anita-header-left">
              <div className="anita-avatar">A</div>
              <div className="anita-header-info">
                <div className="anita-header-title">Anita AI {'  \u2014  '}UNG</div>
                <div className="anita-header-badge">
                  <span className="anita-online-dot"></span> En ligne
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button className="anita-clear-btn" onClick={clearConversation} title="Effacer la conversation">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/>
                  <polyline points="1 20 1 14 7 14"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
              </button>
              <button className="anita-close-btn" onClick={() => setIsOpen(false)} title="Fermer">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#fff">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="anita-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`anita-msg anita-msg-${msg.role}`}>
                {msg.content}
              </div>
            ))}
            {isTyping && (
              <div className="anita-typing">
                <span></span><span></span><span></span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="anita-input-area">
            <textarea
              ref={inputRef}
              className="anita-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Posez votre question..."
              rows={1}
            />
            <button className="anita-send-btn" onClick={sendMessage} disabled={isTyping || !input.trim()}>
              {'\u27a4'}
            </button>
          </div>

          <div className="anita-powered">
            {'Propuls\u00e9 par '}
            <a href="https://hyphentech.africa" target="_blank" rel="noopener noreferrer">Hyphen Tech</a>
          </div>
        </div>
      )}

      <div className={`anita-fab-wrapper${isOpen ? ' anita-fab-hidden' : ''}`}>
        {!isOpen && (
          <div className="anita-tooltip">
            {'\ud83e\udd16 Comment puis-je vous aider ?'}
          </div>
        )}
        <button
          className={`anita-btn${isOpen ? ' anita-btn-open' : ''}`}
          onClick={() => setIsOpen(o => !o)}
          title="Anita AI"
        >
          {isOpen ? (
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.04 2 11c0 2.76 1.44 5.22 3.68 6.82V22l3.85-2.12C10.34 19.96 11.16 20 12 20c5.52 0 10-4.04 10-9S17.52 2 12 2zm1 12h-2v-2h2v2zm0-4h-2V6h2v4z"/>
            </svg>
          )}
        </button>
      </div>
    </>
  );
}
